import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { HeadingDossier } from '../design-system/Headings';
import Paragraph from '../design-system/Paragraph';
import { LinkButton } from '../design-system/Buttons';
import { Link } from '../design-system/Links';
import { formatPriceInUSD } from '../lib/currency';
import DownloadingModal from '../pages/Dashboard/DownloadingModal';

// Taken from https://app.contentful.com/spaces/gtqjh9j40j4m/assets/ZmUL8AbWnD7AUmMYSe6eZ?focusedField=file&focusedLocale=en-US
const DOSSIER_IMAGE = '//images.ctfassets.net/gtqjh9j40j4m/ZmUL8AbWnD7AUmMYSe6eZ/df5493643ee396a666742ab29547a77a/Dossier_print_promo_mockup.png';

const DossierAd = ({
  dashboardPath,
  buyBtnPath,
  previewLink,
  phrases,
  priceCents,
}) => {
  const priceUsd = formatPriceInUSD(priceCents);
  const priceLabel = `${phrases.buyButtonLabel} - ${priceUsd}`;
  const [downloadingModalVisible, setDownloadingModalVisible] = useState(false);

  const showModal = () => {
    setDownloadingModalVisible(true);
  };

  return (
    <div
      className={clsx(
        'ss_magazine-spread',
        'ss_row-reverse',
        'ss_magazine-spread--dossier',
      )}
    >
      {/* desktop image */}
      <img
        src={DOSSIER_IMAGE}
        alt="Dossier book"
        className={clsx(
          'd-none',
          'd-md-block',
          'ss_magazine-spread__img',
          'ss_magazine-spread__img--sml',
          'ss_magazine-spread__img--dossier',
          'ss_magazine-spread__img--letterbox',
        )}
      />
      <div
        className={clsx(
          'ss_magazine-spread__copy',
          'ss_magazine-spread__copy--lg',
        )}
      >
        <HeadingDossier dark el="h1">
          {phrases.title}
        </HeadingDossier>
        <Paragraph dossierIntro dark>
          {phrases.pitch}
        </Paragraph>
        {/* mobile image */}
        <img
          src={DOSSIER_IMAGE}
          alt="Dossier book"
          className={clsx(
            'pt-4',
            'px-3',
            'd-md-none',
            'ss_magazine-spread__img',
            'ss_magazine-spread__img--sml',
            'ss_magazine-spread__img--dossier',
            'ss_magazine-spread__img--letterbox',
          )}
        />
        {/* mobile buttons */}
        <div className="d-xl-none mt-md-3">
          {buyBtnPath && (
            <LinkButton dossierPrimary className="w-100" href={buyBtnPath}>
              {priceLabel}
            </LinkButton>
          )}
          <LinkButton
            dossierPrimary={!buyBtnPath}
            dossierSecondary={buyBtnPath}
            className="w-100 mx-xl-4"
            href={previewLink}
            onClick={showModal}
          >
            {phrases.previewButtonLabel}
          </LinkButton>
        </div>
        {/* desktop buttons */}
        <div className="d-none d-xl-flex flex-row mt-3">
          {buyBtnPath && (
            <LinkButton dossierPrimary href={buyBtnPath}>
              {priceLabel}
            </LinkButton>
          )}
          <LinkButton
            dossierPrimary={!buyBtnPath}
            dossierSecondary={buyBtnPath}
            className={clsx({ 'mx-xl-3': !!buyBtnPath })}
            href={previewLink}
            onClick={showModal}
          >
            {phrases.previewButtonLabel}
          </LinkButton>
        </div>
        {dashboardPath && (
          <Paragraph dossierBodyHeavy dark>
            <Link href={dashboardPath}>
              {phrases.notInterested}
              {phrases.online}
              {' →'}
            </Link>
          </Paragraph>
        )}
      </div>
      {downloadingModalVisible && (
        <DownloadingModal
          visible
          onCloseClick={() => setDownloadingModalVisible(false)}
          phrases={{
            title: phrases.modalTitle,
            description: phrases.modalDescription,
            buttonLabel: phrases.modalButtonLabel,
          }}
        />
      )}
    </div>
  );
};

DossierAd.defaultProps = {
  dashboardPath: null,
  buyBtnPath: null,
};

DossierAd.propTypes = {
  buyBtnPath: PropTypes.string,
  previewLink: PropTypes.string.isRequired,
  dashboardPath: PropTypes.string,
  phrases: PropTypes.exact({
    title: PropTypes.string,
    pitch: PropTypes.string,
    buyButtonLabel: PropTypes.string,
    previewButtonLabel: PropTypes.string,
    notInterested: PropTypes.string,
    online: PropTypes.string,
    modalTitle: PropTypes.string,
    modalDescription: PropTypes.string,
    modalButtonLabel: PropTypes.string,
  }).isRequired,
  priceCents: PropTypes.number.isRequired,
};

export default DossierAd;
